import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTenantId } from './useTenantId';

export interface PartnerRating {
  id: string;
  company_id: string;
  tenant_id: string;
  project_id?: string;
  safety_rating?: number;
  safety_notes?: string;
  quality_rating?: number;
  quality_notes?: string;
  reliability_rating?: number;
  reliability_notes?: string;
  overall_rating?: number;
  overall_notes?: string;
  rating_period_start?: string;
  rating_period_end?: string;
  contract_value?: number;
  rated_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface RatingFormData {
  project_id?: string;
  safety_rating: number;
  safety_notes?: string;
  quality_rating: number;
  quality_notes?: string;
  reliability_rating: number;
  reliability_notes?: string;
  overall_notes?: string;
  rating_period_start?: string;
  rating_period_end?: string;
  contract_value?: number;
}

export function usePartnerRatings(companyId: string | null) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantId();

  const { data: ratings = [], isLoading } = useQuery({
    queryKey: ['partner-ratings', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('partner_ratings')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PartnerRating[];
    },
    enabled: !!companyId,
  });

  // Get rating weights from tenant settings
  const { data: weights } = useQuery({
    queryKey: ['rating-weights', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      
      const settingsData = data?.settings as any;
      const partnerSettings = settingsData?.partner_settings || {};
      let ratingWeights = partnerSettings.rating_weights || {
        safety_weight: 0.40,
        quality_weight: 0.40,
        reliability_weight: 0.20,
      };
      
      // Auto-convert if weights are in percentage form (backward compatibility)
      const weightSum = ratingWeights.safety_weight + ratingWeights.quality_weight + ratingWeights.reliability_weight;
      if (weightSum > 5) {
        console.warn('Auto-converting weights from percentage to decimal form:', ratingWeights);
        ratingWeights = {
          safety_weight: ratingWeights.safety_weight / 100,
          quality_weight: ratingWeights.quality_weight / 100,
          reliability_weight: ratingWeights.reliability_weight / 100,
        };
      }
      
      console.log('Rating weights loaded (decimal form):', ratingWeights);
      return ratingWeights;
    },
    enabled: !!tenantId,
  });

  const calculateOverallRating = (safety: number, quality: number, reliability: number) => {
    if (!weights) {
      console.error('No weights available for calculation');
      return 0;
    }
    
    console.log('Calculating overall rating:', { safety, quality, reliability, weights });
    
    // Validate inputs are in valid range (1-5)
    if (safety < 1 || safety > 5 || quality < 1 || quality > 5 || reliability < 1 || reliability > 5) {
      console.error('Invalid rating values:', { safety, quality, reliability });
      throw new Error('Ratings must be between 1 and 5');
    }
    
    // Double-check weights are in decimal form
    const weightSum = weights.safety_weight + weights.quality_weight + weights.reliability_weight;
    if (weightSum > 5) {
      console.error('Weights are STILL in percentage form!', weights);
      throw new Error('Rating weights must be in decimal form (0-1), not percentages');
    }
    
    const overall = (
      safety * weights.safety_weight +
      quality * weights.quality_weight +
      reliability * weights.reliability_weight
    );
    
    console.log('Calculated overall rating:', { 
      calculation: `${safety} * ${weights.safety_weight} + ${quality} * ${weights.quality_weight} + ${reliability} * ${weights.reliability_weight}`,
      result: overall 
    });
    
    // Clamp to valid range as safety measure
    const clamped = Math.max(1, Math.min(5, overall));
    
    if (clamped !== overall) {
      console.warn('Overall rating was clamped:', { original: overall, clamped });
    }
    
    const rounded = Math.round(clamped * 100) / 100;
    console.log('Final overall rating:', rounded);
    
    return rounded;
  };

  const createRating = useMutation({
    mutationFn: async ({ companyId, data }: { companyId: string; data: RatingFormData }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const overallRating = calculateOverallRating(
        data.safety_rating,
        data.quality_rating,
        data.reliability_rating
      );

      const insertData: any = {
        company_id: companyId,
        tenant_id: tenantId,
        rated_by_user_id: user.id,
        overall_rating: overallRating,
        safety_rating: data.safety_rating,
        quality_rating: data.quality_rating,
        reliability_rating: data.reliability_rating,
      };

      if (data.safety_notes) insertData.safety_notes = data.safety_notes;
      if (data.quality_notes) insertData.quality_notes = data.quality_notes;
      if (data.reliability_notes) insertData.reliability_notes = data.reliability_notes;
      if (data.overall_notes) insertData.overall_notes = data.overall_notes;
      if (data.project_id) insertData.project_id = data.project_id;
      if (data.rating_period_start) insertData.rating_period_start = data.rating_period_start;
      if (data.rating_period_end) insertData.rating_period_end = data.rating_period_end;
      if (data.contract_value) insertData.contract_value = data.contract_value;

      const { data: rating, error } = await supabase
        .from('partner_ratings')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return rating;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-ratings', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-performance'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-trends'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-documents'] });
      toast.success('Rating added successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add rating');
    },
  });

  const updateRating = useMutation({
    mutationFn: async ({ ratingId, data }: { ratingId: string; data: Partial<RatingFormData> }) => {
      let updates: any = { ...data };
      
      // Recalculate overall if any component rating changed
      const rating = ratings.find(r => r.id === ratingId);
      if (rating && (data.safety_rating || data.quality_rating || data.reliability_rating)) {
        const safety = data.safety_rating ?? rating.safety_rating ?? 0;
        const quality = data.quality_rating ?? rating.quality_rating ?? 0;
        const reliability = data.reliability_rating ?? rating.reliability_rating ?? 0;
        updates.overall_rating = calculateOverallRating(safety, quality, reliability);
      }

      const { data: updatedRating, error } = await supabase
        .from('partner_ratings')
        .update(updates)
        .eq('id', ratingId)
        .select()
        .single();

      if (error) throw error;
      return updatedRating;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-ratings', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-performance'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-trends'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-documents'] });
      toast.success('Rating updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update rating');
    },
  });

  const deleteRating = useMutation({
    mutationFn: async (ratingId: string) => {
      const { error } = await supabase
        .from('partner_ratings')
        .delete()
        .eq('id', ratingId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-ratings', companyId] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-performance'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-trends'] });
      queryClient.invalidateQueries({ queryKey: ['partner-analytics-documents'] });
      toast.success('Rating deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete rating');
    },
  });

  const averageRatings = ratings.length > 0 ? {
    safety: ratings.reduce((sum, r) => sum + (r.safety_rating || 0), 0) / ratings.length,
    quality: ratings.reduce((sum, r) => sum + (r.quality_rating || 0), 0) / ratings.length,
    reliability: ratings.reduce((sum, r) => sum + (r.reliability_rating || 0), 0) / ratings.length,
    overall: ratings.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / ratings.length,
  } : null;

  return {
    ratings,
    isLoading,
    averageRatings,
    weights,
    calculateOverallRating,
    createRating: createRating.mutateAsync,
    updateRating: updateRating.mutateAsync,
    deleteRating: deleteRating.mutateAsync,
  };
}
